import type { Module } from 'vuex';
import { IMenuItem } from '../../entities/menu-item-entity';
import { IPage } from '../../entities/cms-page-entity';

export const namespace = 'cms';

interface ILoadingState {
	page: boolean;
	menu: boolean;
}

export interface IGetPageParams {
	pageIdOrName: string | number | null;
	contentOnly?: boolean;
}

export interface ICmsState {
	page: null | IPage;
	loading: ILoadingState;
	error: unknown;
	menu: IMenuItem[];
	menus: Record<string, any>;
	flatMenu: Record<string, any>[];
}

export const MutationType = {
	SET_PAGE: 'setPage',
	SET_LOADING: 'setPageLoadin',
	SET_ERROR: 'setError',
	SET_MENU: 'setMenu',
	SET_ALL_MENUS: 'setAllMenu',
	SET_FLAT_MENU: 'setFlatMenu',
};

export const actionType = {
	GET_PAGE: 'getPage',
	GET_PAGE_BY_ID: 'getPageById',
	GET_MENU: 'getMenu',
	RESET_STATE: 'resetState',
};

const makeMainMenu = (items: any[]): IMenuItem[][] => {
	let menu = items;
	menu.sort((i1, i2) => i1.index - i2.index);

	const homepageMenu = menu.find((m) => m.path === 'homepage');
	if (homepageMenu) {
		homepageMenu.path = '';
	}

	const unfilteredMenu = [...menu];
	menu = menu.filter((i) => !i.isHidden);

	return [menu, unfilteredMenu];
};

const bfs = (menu: IMenuItem[]) => {
	const menus: IMenuItem[] = [];
	const queue: IMenuItem[] = menu;

	while (queue.length) {
		const cur = queue.pop();
		if (!cur) {
			// eslint-disable-next-line no-continue
			continue;
		}

		menus.push(cur);

		cur.children.forEach((child) => queue.unshift(child));
	}

	return menus;
};

export default function cartStore(): Module<ICmsState, any> {
	return {
		namespaced: true,
		state: () => ({
			page: null,
			loading: {
				page: false,
				menu: false,
			},
			error: null,
			menus: {},
			menu: [],
			flatMenu: [],
		}),
		actions: {
			resetState({ commit }) {
				commit(MutationType.SET_PAGE, null);
			},

			async [actionType.GET_MENU]({ commit }): Promise<any[]> {
				try {
					commit('appState/setLoading', { menu: true }, { root: true });
					const res = await this.$api.content.getContentMenus.execute({});
					/** NOTE: определить общий метод для меню, возможно с кешем в локал сторадж */
					if (res.isSuccess) {
						const menus = res.getValue();

						commit(MutationType.SET_ALL_MENUS, menus?.contentMenus || {});
						if (menus?.contentMenus && menus?.contentMenus.default) {
							const [menu, unfilteredMenu] = makeMainMenu(menus?.contentMenus.default.items);
							const flattenedMenus = bfs([...unfilteredMenu]);

							commit(MutationType.SET_MENU, menu);
							commit(MutationType.SET_FLAT_MENU, flattenedMenus);
						}
						return menus;
					}
					return [];
				} finally {
					commit('appState/setLoading', { menu: false }, { root: true });
				}
			},
			async [actionType.GET_PAGE_BY_ID]({ commit }, pageId: number): Promise<IPage | undefined> {
				try {
					commit('appState/setLoading', { cmsPage: true }, { root: true });
					const res = await this.$api.content.getContentPageById.execute({
						pageId,
					});
					if (res.isSuccess) {
						const page = res.getValue();
						return page.content;
					}
					return undefined;
				} catch (error) {
					// eslint-disable-next-line no-console
					commit('appState/setLoading', { cmsPage: false }, { root: true });
					return undefined;
				}
			},
			async [actionType.GET_PAGE](
				{ commit },
				{ pageIdOrName, contentOnly = false }: IGetPageParams,
			): Promise<IPage | undefined> {
				let res: any = null;
				try {
					commit(MutationType.SET_LOADING, { key: 'page', value: true });
					commit('appState/setLoading', { cmsPage: true }, { root: true });

					if (Number.isInteger(pageIdOrName)) {
						res = await this.$api.content.getContentPageById.execute({
							pageId: pageIdOrName as number,
						});
					} else if (typeof pageIdOrName === 'string') {
						res = await this.$api.content.getContentPage.execute({
							pageSlug: pageIdOrName,
						});
					}

					if (!res.isSuccess) {
						return undefined;
					}

					const page = res.getValue().content;

					if (contentOnly) {
						return page;
					}

					commit(MutationType.SET_PAGE, page);
					return page;
				} catch (e) {
					commit(MutationType.SET_ERROR, e);
					return undefined;
				} finally {
					commit(MutationType.SET_LOADING, { key: 'page', value: false });
					commit('appState/setLoading', { cmsPage: false }, { root: true });
				}
			},
		},
		mutations: {
			[MutationType.SET_PAGE]: (state, page: null | IPage) => {
				state.page = page;
			},
			[MutationType.SET_LOADING]: (state, { value }: { prop: string; value: boolean }) => {
				state.loading.menu = value;
			},
			[MutationType.SET_MENU]: (state, menu: IMenuItem[]) => {
				state.menu = menu;
			},
			[MutationType.SET_FLAT_MENU]: (state, flatMenu: IMenuItem[]) => {
				state.flatMenu = flatMenu;
			},
			[MutationType.SET_ALL_MENUS]: (state, menus) => {
				state.menus = menus;
			},
			[MutationType.SET_ERROR]: (state, err: unknown) => {
				state.error = err;
			},
		},
		getters: {
			page: ({ page }) => page,
			menu: ({ menu }) => menu,
			flatMenu: ({ flatMenu }) => flatMenu,
			allMenus: ({ menus }) => menus,
			getMenu:
				({ menus }) =>
				(name: string) =>
					menus[name],
		},
	};
}
